import { BbResizeHandlesComponent, BbResizeHandleDragEvent } from './../components/shared/bb-resize-handles/bb-resize-handles.component';
import { Directive, ElementRef, Input, Output, EventEmitter, Renderer2, NgZone, ComponentRef, AfterViewInit, OnDestroy } from '@angular/core';
import { modKeyDown } from 'app/shared/utils/mod-key-down.util';

export interface BbResizeEvent {
	left: number;
	top: number;
	width: number;
	height: number;
	leftRatio?: number;
	topRatio?: number;
	widthRatio?: number;
	heightRatio?: number;
	type: 'resizestarted' | 'resizeended' | 'resized';
	offset: { // distance from start
		x: number;
		y: number;
	};
	source: {
		event: MouseEvent;
		element: HTMLElement;
	};
	snappedX: boolean;
	snappedY: boolean;
	snapValues?: any;
	relativeSnapPointOffsetLeft: number;
	relativeSnapPointOffsetTop: number;
	direction?: string;
}

export interface BbSnapCoords {
	points: any; // x652: number, y204: number, etc.
	pointDistance: number;
	lines: {
		x: any; // x652: number, x804: number, etc.
		y: any; // y0: number, x804: number, etc.
	};
	lineDistance: number;
}

@Directive({
	selector: '[bbResize]'
})

export class BbResizeDirective implements AfterViewInit, OnDestroy {
	@Output() bbResizeStarted: EventEmitter<BbResizeEvent> = new EventEmitter<BbResizeEvent>();
	@Output() bbResized: EventEmitter<BbResizeEvent> = new EventEmitter<BbResizeEvent>();
	@Output() bbResizeEnded: EventEmitter<BbResizeEvent> = new EventEmitter<BbResizeEvent>();

	@Input('bbResizeResizingClass') resizingClass = 'bb-resizing';
	@Input('bbDragAxis') axisLock: 'x' | 'y';
	@Input('bbResizeContainer') containerElement: HTMLElement;
	@Input('bbResizeConstraint') constraintElement: HTMLElement;
	// if true, when dragging x (e, se) also make sure you don't cross boundary on y axis
	@Input('bbResizeConstrainAspectRatio') constrainAspectRatio = false;

	@Input('bbResizeHandles') resizeHandles: BbResizeHandlesComponent;
	@Input('bbResizeContainerQuery') containerQuery: string;
	@Input('bbResizeDisabled') disabled = false;
	@Input('bbResizeUsePct') usePct = false;
	@Input('bbResizeLockAspectRatio') lockAspectRatio = false;
	@Input('bbResizeUseSnapping') useSnapping = false;
	@Input('bbResizeSnapCoords') snapCoords: any;
	// default: snap only to topleft. x and y can range from 0 to 1 (1,1 being the bottom-right)
	@Input('bbResizeRelativeSnapPoints') relativeSnapPoints: any = [{ x: 0, y: 0 }];
	@Input('bbResizePointSnapDistance') pointSnapDistance = 10;
	@Input('bbResizeLineSnapDistance') lineSnapDistance = 8;
	@Input('bbResizeShowSnapGuides') showSnapGuides = false;
	@Input('bbResizeMinSize') minSize = 2;

	resizeEl: HTMLElement;

	resizeHandlesComponentRef: ComponentRef<BbResizeHandlesComponent>;
	resizeDirection = '';
	elStartProps: DOMRect | ClientRect;
	containerStartProps: DOMRect | ClientRect;
	constraintStartProps: DOMRect | ClientRect;
	mouseOffset: any;
	mouseMoveListener: any;
	mouseUpListener: any;
	docMouseUpListener: any;
	dragStartListener: any;
	dragging = false;
	snapLineX: HTMLElement;
	snapLineY: HTMLElement;
	resizeElement: HTMLElement;

	preDragUserSelect: any;

	private previousResizeAction: BbResizeEvent;

	constructor(
		private el: ElementRef,
		private ngZone: NgZone,
		private renderer: Renderer2
	) { }

	// @HostListener('mousedown', ['$event'])
	onMouseDown(ev: MouseEvent) {
		this.ngZone.runOutsideAngular(() => {
			if (ev.button === 0) {
				this.setSnapLinesSize();
				this.stopListeners();
				if (!this.disabled) {
					this.dragging = true;
					this.elStartProps = this.el.nativeElement.getBoundingClientRect();
					this.containerStartProps = this.containerElement.getBoundingClientRect();
					this.constraintStartProps = this.constraintElement.getBoundingClientRect();
					this.mouseOffset = {
						left: ev.pageX - this.elStartProps.left,
						top: ev.pageY - this.elStartProps.top,
					};
					this.mouseMoveListener = this.renderer.listen(document, 'mousemove', (mev: MouseEvent) => {
						this.onMouseMove(mev);
					});
					this.mouseUpListener = this.renderer.listen(this.resizeEl, 'mouseup', (mev: MouseEvent) => {
						this.onMouseUp(mev);
					});
					this.docMouseUpListener = this.renderer.listen(document, 'mouseup', (mev: MouseEvent) => {
						this.onDocumentMouseUp(mev);
					});

					const bbResizeEvent = this.createBbResizeEvent(ev);
					this.preDragUserSelect = document.body.style.userSelect;
					document.body.style.userSelect = 'none';
					this.resizeEl.style.userSelect = 'none';

					this.resizeEl.classList.add(this.resizingClass);
					this.bbResizeStarted.emit(bbResizeEvent);
				}
			}
		});
	}

	ngAfterViewInit() {
		this.resizeEl = this.el.nativeElement;
		this.initializeResizeHandles();
		if (this.containerElement === null && this.containerQuery) {
			this.containerElement = this.resizeEl.closest(this.containerQuery) as HTMLElement;
		}
		this.constraintElement = this.constraintElement || this.containerElement;
		this.resizeElement = this.resizeEl.parentElement;
		this.initializeSnapGuides();
	}

	ngOnDestroy() {
		this.stopListeners();
		if (this.dragStartListener) {
			this.dragStartListener();
		}
	}

	stopListeners() {
		// removes listeners
		if (this.mouseMoveListener) {
			this.mouseMoveListener();
		}
		if (this.mouseUpListener) {
			this.mouseUpListener();
		}
		if (this.docMouseUpListener) {
			this.docMouseUpListener();
		}
	}

	stopResize(ev: MouseEvent) {
		this.stopListeners();
		if (this.dragging) {
			this.dragging = false;
			this.resizeEl.classList.remove(this.resizingClass);
			if (this.preDragUserSelect) {
				document.body.style.userSelect = this.preDragUserSelect;
				this.preDragUserSelect = null;
			}
			const bbResizeEvent = this.createBbResizeEvent(ev);
			this.hideSnapLines();
			if (bbResizeEvent) {
				this.bbResizeEnded.emit(bbResizeEvent);
			}
		}
	}


	private initializeResizeHandles() {
		const resizeHandlesInstance = this.resizeHandles;
		if (!resizeHandlesInstance) {
			return;
		}

		resizeHandlesInstance.mouseDownEvent.subscribe((resizeMouseEvent) => {
			this.handleResizePointMouseDownEvent(resizeMouseEvent);
		});
	}

	private handleResizePointMouseDownEvent(rhde: BbResizeHandleDragEvent) {
		this.resizeDirection = rhde.direction;
		rhde.mouseEvent.stopPropagation();
		this.onMouseDown(rhde.mouseEvent);
	}

	private initializeSnapGuides() {
		if (this.showSnapGuides && this.containerElement) {
			this.snapLineX = this.containerElement.querySelector('.bb-drag-snap-line-x');
			this.snapLineY = this.containerElement.querySelector('.bb-drag-snap-line-y');
			if (this.snapLineX && this.snapLineY) {
				return;
			} else {
				this.snapLineX = document.createElement('div');
				this.snapLineY = document.createElement('div');
			}
			const initialStyles = {
				position: 'absolute',
				pointerEvents: 'none',
				display: 'none',
				left: 0,
				top: 0,
				zIndex: 10,
				backgroundColor: 'rgba(255, 0, 0, 0.8)',
			};
			for (const prop of Object.keys(initialStyles)) {
				this.snapLineX.style[prop.toString()] = initialStyles[prop.toString()];
				this.snapLineY.style[prop.toString()] = initialStyles[prop.toString()];
			}
			this.snapLineX.style.height = '1px';
			this.snapLineY.style.width = '1px';
			this.setSnapLinesSize();
			this.snapLineX.classList.add('bb-drag-snap-line-x');
			this.snapLineY.classList.add('bb-drag-snap-line-y');
			this.containerElement.appendChild(this.snapLineX);
			this.containerElement.appendChild(this.snapLineY);
		}
	}

	private setSnapLinesSize() {
		if (this.snapLineX && this.snapLineY) {
			this.snapLineX.style.width = this.containerElement.offsetWidth + 'px';
			this.snapLineY.style.height = this.containerElement.offsetHeight + 'px';
		}
	}

	private updateSnapLines(bbResizeEvent: BbResizeEvent) {
		if (this.snapLineY) {
			if (bbResizeEvent.snappedX) {
				this.snapLineY.style.left = bbResizeEvent.left + bbResizeEvent.relativeSnapPointOffsetLeft + 'px';
				this.snapLineY.style.display = 'block';
			} else {
				this.snapLineY.style.display = 'none';
			}
		}

		if (this.snapLineX) {
			if (bbResizeEvent.snappedY) {
				this.snapLineX.style.top = bbResizeEvent.top + bbResizeEvent.relativeSnapPointOffsetTop + 'px';
				this.snapLineX.style.display = 'block';
			} else {
				this.snapLineX.style.display = 'none';
			}
		}
	}

	private hideSnapLines() {
		if (this.snapLineX && this.snapLineY) {
			this.snapLineY.style.display = 'none';
			this.snapLineX.style.display = 'none';
		}
	}

	private onMouseMove(ev: MouseEvent) {
		this.ngZone.runOutsideAngular(() => {
			const bbResizeEvent = this.createBbResizeEvent(ev);
			this.updateSnapLines(bbResizeEvent);

			if (this.axisLock !== 'y') {
				if (this.usePct) {
					if (!bbResizeEvent.direction.includes('e')) {
						this.renderer.setStyle(this.el.nativeElement, 'left', (bbResizeEvent.left / this.containerElement.offsetWidth * 100) + '%');
					}
					this.renderer.setStyle(this.el.nativeElement, 'width', (bbResizeEvent.width / this.containerElement.offsetWidth * 100) + '%');
				} else {
					if (!bbResizeEvent.direction.includes('e')) {
						this.renderer.setStyle(this.el.nativeElement, 'left', bbResizeEvent.left + 'px');
					}
					this.renderer.setStyle(this.el.nativeElement, 'width', bbResizeEvent.width + 'px');
				}
			}
			if (this.axisLock !== 'x') {
				if (this.usePct) {
					if (!bbResizeEvent.direction.includes('s')) {
						this.renderer.setStyle(this.el.nativeElement, 'top', (bbResizeEvent.top / this.containerElement.offsetHeight * 100) + '%');
					}
					this.renderer.setStyle(this.el.nativeElement, 'height', (bbResizeEvent.height / this.containerElement.offsetHeight * 100) + '%');
				} else {
					if (!bbResizeEvent.direction.includes('s')) {
						this.renderer.setStyle(this.el.nativeElement, 'top', bbResizeEvent.top + 'px');
					}
					this.renderer.setStyle(this.el.nativeElement, 'height', bbResizeEvent.height + 'px');
				}
			}
			this.bbResized.emit(bbResizeEvent);
		});
	}

	private onMouseUp(ev: MouseEvent) {
		this.stopResize(ev);
	}

	private onDocumentMouseUp(ev: MouseEvent) {
		this.stopResize(ev);
	}

	private createBbResizeEvent(ev: MouseEvent): BbResizeEvent {
		const elRect = this.el.nativeElement.getBoundingClientRect();
		const modKey = modKeyDown(ev);
		const useSnapping = modKey ? !this.useSnapping : this.useSnapping;
		const offsetX = elRect.left - this.elStartProps.left;
		const offsetY = elRect.top - this.elStartProps.top;
		const containerTop = this.containerElement.getBoundingClientRect().top;
		const containerLeft = this.containerElement.getBoundingClientRect().left;

		const re: BbResizeEvent = {
			type: ev.type === 'mousedown' ? 'resizestarted' : (ev.type === 'mouseup' ? 'resizeended' : 'resized'),
			left: this.elStartProps.left,
			top: this.elStartProps.top,
			width: this.elStartProps.width,
			height: this.elStartProps.height,
			offset: {
				x: offsetX,
				y: offsetY
			},
			source: {
				event: ev,
				element: this.el.nativeElement
			},
			snappedX: false,
			snappedY: false,
			snapValues: {},
			relativeSnapPointOffsetLeft: 0,
			relativeSnapPointOffsetTop: 0,
			direction: this.resizeDirection
		};

		// resizing
		if (this.resizeDirection.includes('n')) {
			re.top = ev.pageY - this.mouseOffset.top;

			// stay within constraintElement
			if (re.top < this.constraintStartProps.top) {
				re.top = this.constraintStartProps.top;
			}
			re.height = this.elStartProps.top - re.top + this.elStartProps.height;
			const maxTop = (this.elStartProps.top + this.elStartProps.height) - this.minSize;
			if (re.top > maxTop) {
				re.top = maxTop;
			}
		}

		if (this.resizeDirection.includes('e')) {
			re.width = ev.pageX - this.elStartProps.left;
		}

		if (this.resizeDirection.includes('s')) {
			re.height = ev.pageY - this.elStartProps.top;
		}

		if (this.resizeDirection.includes('w')) {
			re.left = ev.pageX - this.mouseOffset.left;
			// stay within constraintElement
			if (re.left < this.constraintStartProps.left) {
				re.left = this.constraintStartProps.left;
			}
			re.width = this.elStartProps.left - re.left + this.elStartProps.width;
			const maxLeft = (this.elStartProps.left + this.elStartProps.width) - this.minSize;
			if (re.left > maxLeft) {
				re.left = maxLeft;
			}
		}

		// stay within constraintElement
		if (this.resizeDirection.includes('e') && re.left + this.elStartProps.width > this.constraintStartProps.left + this.constraintStartProps.width) {
			re.left = this.constraintStartProps.left + this.constraintStartProps.width - this.elStartProps.width;
		}
		if (this.resizeDirection.includes('s') && re.top + this.elStartProps.height > this.constraintStartProps.top + this.constraintStartProps.height) {
			re.top = this.constraintStartProps.top + this.constraintStartProps.height - this.elStartProps.height;
		}

		if (re.left + re.width > this.constraintStartProps.left + this.constraintStartProps.width) {
			re.width = (this.constraintStartProps.left + this.constraintStartProps.width) - re.left;
		}
		if (re.top + re.height > this.constraintStartProps.top + this.constraintStartProps.height) {
			re.height = (this.constraintStartProps.top + this.constraintStartProps.height) - re.top;
		}

		if (re.width < this.minSize) {
			re.width = this.minSize;
		}
		if (re.height < this.minSize) {
			re.height = this.minSize;
		}

		re.left = re.left - containerLeft;
		re.top = re.top - containerTop;

		// snapping
		if (useSnapping) {
			let leftSnap = re.left;
			let topSnap = re.top;
			let widthSnap = re.width;
			let heightSnap = re.height;
			if (this.snapCoords && this.snapCoords.length) {
				for (let i = 0; i < this.snapCoords.length; i++) {
					const snapCoord = this.snapCoords[i];
					const snapRange = snapCoord.x !== undefined && snapCoord.y !== undefined ? this.pointSnapDistance : this.lineSnapDistance;
					if (snapCoord.x !== undefined || snapCoord.y !== undefined) {
						let minSnapDistanceX = Infinity;
						let minSnapDistanceY = Infinity;
						if (this.relativeSnapPoints && this.relativeSnapPoints.length) {
							for (let j = 0; j < this.relativeSnapPoints.length; j++) {
								const relativeSnapPoint = this.relativeSnapPoints[j];
								if (relativeSnapPoint.x !== undefined && snapCoord.x !== undefined && this.resizeDirection.includes('w')) {
									const xExtra = (this.elStartProps.width * relativeSnapPoint.x);
									const xCoord = xExtra + re.left;
									const distanceX = Math.abs(snapCoord.x - xCoord);
									if (distanceX <= snapRange && distanceX < minSnapDistanceX) {
										minSnapDistanceX = distanceX;
										leftSnap = snapCoord.x - xExtra;
										re.snapValues['x' + String(relativeSnapPoint.x)] = snapCoord.xValue;
										re.relativeSnapPointOffsetLeft = xExtra;
									}
								} else if (snapCoord.x !== undefined && this.resizeDirection.includes('e')) {
									const xExtra = re.width;
									const xCoord = xExtra + re.left;
									const distanceX = Math.abs(snapCoord.x - xCoord);
									if (distanceX <= snapRange && distanceX < minSnapDistanceX) {
										minSnapDistanceX = distanceX;
										widthSnap = snapCoord.x - re.left;
										re.snapValues.x1 = snapCoord.xValue;
										re.relativeSnapPointOffsetLeft = widthSnap;
									}
								}
								if (relativeSnapPoint.y !== undefined && snapCoord.y !== undefined &&
									(
										!this.lockAspectRatio && this.resizeDirection.includes('n') ||
										this.lockAspectRatio && this.resizeDirection === 'n'
									)
								) {
									const yExtra = (this.elStartProps.height * relativeSnapPoint.y);
									const yCoord = yExtra + re.top;
									const distanceY = Math.abs(snapCoord.y - yCoord);
									if (distanceY <= snapRange && distanceY < minSnapDistanceY) {
										minSnapDistanceY = distanceY;
										topSnap = snapCoord.y - yExtra;
										re.snapValues['y' + String(relativeSnapPoint.y)] = snapCoord.yValue;
										re.relativeSnapPointOffsetTop = yExtra;
									}
								} else if (snapCoord.y !== undefined &&
									(
										!this.lockAspectRatio && this.resizeDirection.includes('s') ||
										this.lockAspectRatio && this.resizeDirection === 's'
									)) {
									const yExtra = re.height;
									const yCoord = yExtra + re.top;
									const distanceY = Math.abs(snapCoord.y - yCoord);
									if (distanceY <= snapRange && distanceY < minSnapDistanceY) {
										minSnapDistanceY = distanceY;
										heightSnap = snapCoord.y - re.top;
										re.snapValues.y1 = snapCoord.yValue;
										re.relativeSnapPointOffsetTop = heightSnap;
									}
								}
							}
						}
					}
				}
			}

			if (this.resizeDirection.includes('w') && re.left !== leftSnap) {
				re.width = re.width + (re.left - leftSnap);
				re.left = leftSnap;
				re.snappedX = true;
			}
			if (this.resizeDirection.includes('e') && re.width !== widthSnap) {
				re.width = widthSnap;
				re.snappedX = true;
			}

			if (this.resizeDirection.includes('n') && re.top !== topSnap) {
				re.height = re.height + (re.top - topSnap);
				re.top = topSnap;
				re.snappedY = true;
			}
			if (this.resizeDirection.includes('s') && re.height !== heightSnap) {
				re.height = heightSnap;
				re.snappedY = true;
			}
		}

		if (this.lockAspectRatio) {
			const aspectRatio = this.elStartProps.width / this.elStartProps.height;

			// set width and height according to aspect ratio
			if (['nw', 'ne', 'sw', 'se'].includes(this.resizeDirection)) {
				if (re.snappedY) {
					re.width = re.height * aspectRatio;
					re.snappedX = false;
				} else {
					re.height = re.width / aspectRatio;
				}
			} else if (['e', 'w'].includes(this.resizeDirection)) {
				re.height = re.width / aspectRatio;
			} else if (['s', 'n'].includes(this.resizeDirection)) {
				re.width = re.height * aspectRatio;
			}

			// if required, check if the resized aspectratio fits in the constraintElement
			// - 0.1 to avoid rounding issues for the > compare
			if (this.constrainAspectRatio) {
				if (re.width + re.left > this.constraintStartProps.width) {
					re.width = this.constraintStartProps.width - re.left - 0.1;
					re.height = re.width * (1 / aspectRatio);
					if (this.previousResizeAction && this.resizeDirection === 'n') {
						re.left = this.previousResizeAction.left;
						re.top = this.previousResizeAction.top;
					}
				}

				if (re.height + re.top > this.constraintStartProps.height) {
					if (this.previousResizeAction && this.resizeDirection === 'w') {
						re.left = this.previousResizeAction.left;
						re.top = this.previousResizeAction.top;
					}
					re.height = this.constraintStartProps.height - re.top - 0.1;
					re.width = re.height * aspectRatio;
				}
			}

			// set left and top according to aspect ratio
			if (this.resizeDirection === 'nw') {
				re.top = (this.elStartProps.top + this.elStartProps.height) - re.height - containerTop;
				re.left = (this.elStartProps.left + this.elStartProps.width) - re.width - containerLeft;
			} else if (this.resizeDirection === 'ne') {
				re.top = (this.elStartProps.top + this.elStartProps.height) - re.height - containerTop;
			} else if (this.resizeDirection === 'sw') {
				re.left = (this.elStartProps.left + this.elStartProps.width) - re.width - containerLeft;
			} else if (this.resizeDirection === 'se') {
				// se no influence on left/top
			}

			// move down if it moved out of bounds at top. Other constraints are already handled
			if (this.constrainAspectRatio && re.top < 0) {
				re.top = 0;
			}
		}

		re.widthRatio = re.width / this.constraintStartProps.width;
		re.heightRatio = re.height / this.constraintStartProps.height;
		re.leftRatio = re.left / this.constraintStartProps.width;
		re.topRatio = re.top / this.constraintStartProps.height;

		this.previousResizeAction = re;
		return re;
	}
}
